// src/lib/topPlatesApi.js

// ---------- small utilities ----------
function toQS(params = {}) {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0)) continue;
    if (Array.isArray(v)) p.set(k, v.join(","));
    else p.set(k, String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : "";
}

function coerceIsNight(val) {
  if (val === true || val === 1 || val === "1") return "1";
  if (val === false || val === 0 || val === "0") return "0";
  return undefined;
}

// ---------- API: Top Plates ----------
/**
 * Fetch the "Top Plates" aggregation.
 * Mirrors the legacy page AND matches Sightings param semantics.
 */
export async function fetchTopPlates(query = {}) {
  const {
    period,
    start,
    end,
    buckets,
    is_night,
    cameras,
    limit = 20,
    offset = 0,
  } = query;

  const qs = toQS({
    period,
    start,
    end,
    buckets: Array.isArray(buckets) ? buckets : (buckets ? [buckets] : undefined),
    is_night: coerceIsNight(is_night),
    cameras: Array.isArray(cameras) ? cameras : (cameras ? [cameras] : undefined),
    limit,
    offset,
  });

  const url = `/api/traffic/top-plates${qs}`;
  // Helpful when verifying in DevTools
  // eslint-disable-next-line no-console
  console.debug("[topPlatesApi] GET", url);

  const res = await fetch(url, {
    method: "GET",
    headers: { Accept: "application/json" },
    cache: "no-store",
  });
  if (!res.ok) {
    const msg = await res.text().catch(() => res.statusText);
    throw new Error(`Top Plates fetch failed: ${res.status} ${msg}`);
  }

  const data = await res.json();
  // console.debug("[topPlatesApi] ←", data);
  return data; // explicit return (no inline JSDoc after return)
}

// ---------- API: Latest Media ----------
/**
 * Fetch the latest media for a plate (or exact detection_id).
 *
 * Notes:
 *  - Omit `period` for “Anytime”.
 *  - Use `period=custom` when sending start/end (or infer if only start/end present).
 *  - Send a single `camera=<id>` when restricting to one camera. Do NOT send `cameras`.
 */
export async function fetchLatestMedia(query = {}) {
  const {
    detection_id,
    plate,
    period,
    start,
    end,
    buckets,
    is_night,
    exact,
    camera,
    before,
  } = query;

  // Build a backend-safe query (no `period=anytime`, no `cameras`)
  const q = {};

  if (detection_id != null) q.detection_id = detection_id;
  if (plate) q.plate = plate;

  // time
  if (period && period !== "anytime") {
    q.period = period;
    if (period === "custom") {
      if (start) q.start = start;
      if (end) q.end = end;
    }
  } else if (!period && (start || end)) {
    q.period = "custom";
    if (start) q.start = start;
    if (end) q.end = end;
  }

  // daypart
  const night = coerceIsNight(is_night);
  if (night !== undefined) q.is_night = night;
  else if (buckets) q.buckets = Array.isArray(buckets) ? buckets : [buckets];

  // exact plate
  if (exact === true || exact === "true" || exact === 1) q.exact = "true";

  // single camera only
  if (camera) q.camera = camera;

  if (before) q.before = before;

  const qs = toQS(q);
  const url = `/api/media/latest${qs}`;
  // eslint-disable-next-line no-console
  console.debug("[topPlatesApi] GET", url);

  const res = await fetch(url, {
    method: "GET",
    headers: { Accept: "application/json" },
    cache: "no-store",
  });
  if (!res.ok) {
    const msg = await res.text().catch(() => res.statusText);
    throw new Error(`Latest media fetch failed: ${res.status} ${msg}`);
  }

  const data = await res.json();
  // console.debug("[topPlatesApi] ←", data);
  return data; // explicit return (no inline JSDoc after return)
}
